import * as _ from 'underscore';

import { IExplorableItemTypeInfo } from '../../../ts/models/resourceInfoParts';
import { isSubsetOf, KeySet, makeKeySet } from '../../ItemsSet';

import { ExplorableItemType } from '../../../ts/models/ContentType';
import { CancelBackgroundJobAction } from './EntityActions/CancelBackgroundJobAction';
import { ChangeExtractDsToLiveAction } from './EntityActions/ChangeExtractDsToLiveAction';
import { ChangeExtractRefreshScheduleEntityAction } from './EntityActions/ChangeExtractRefreshScheduleEntityAction';
import { ChangeRemoteExtractRefreshEntityAction } from './EntityActions/ChangeRemoteExtractRefreshEntityAction';
import { ChangeRemoteExtractRefreshExpectedDurationEntityAction } from './EntityActions/ChangeRemoteExtractRefreshExpectedDurationEntityAction';
import { ChangeSubscriptionDataConditionEntityAction } from './EntityActions/ChangeSubscriptionDataConditionEntityAction';
import { ChangeSubscriptionScheduleEntityAction } from './EntityActions/ChangeSubscriptionScheduleEntityAction';
import { ChangeSubscriptionsFormatEntityAction } from './EntityActions/ChangeSubscriptionsFormatEntityAction';
import { ChangeSubscriptionSubjectEntityAction } from './EntityActions/ChangeSubscriptionSubjectEntityAction';
import { CreateExtractsAction } from './EntityActions/CreateExtractsAction';
import { DecryptAction } from './EntityActions/DecryptAction';
import { DeleteAction } from './EntityActions/DeleteAction';
import { DeleteExtractRefreshEntityAction } from './EntityActions/DeleteExtractRefreshEntityAction';
import { DeleteRemoteExtractRefreshEntityAction } from './EntityActions/DeleteRemoteExtractRefreshEntityAction';
import { DownloadEntityAction } from './EntityActions/DownloadEntityAction';
import { EditDataConnectionsAction } from './EntityActions/EditDataConnectionAction';
import { EditFlowEntityAction } from './EntityActions/EditFlowEntityAction';
import { EditViewAction } from './EntityActions/EditViewAction';
import { EditWorkbookAction } from './EntityActions/EditWorkbookAction';
import { EncryptAction } from './EntityActions/EncryptAction';
import { IEntityAction } from './EntityActions/IEntityAction';
import { MoveAction } from './EntityActions/MoveAction';
import { NewMetricEntityAction } from './EntityActions/NewMetricEntityAction';
import { NewWorkbookEntityAction } from './EntityActions/NewWorkbookEntityAction';
import { RefreshExtractsAction } from './EntityActions/RefreshExtractsAction';
import { RemoveExternalContentAction } from './EntityActions/RemoveExternalContentAction';
import { RenameEntityAction } from './EntityActions/RenameEntityAction';
import { ResumeSubscriptionEntityAction } from './EntityActions/ResumeSubscriptionEntityAction';
import { RevisionHistoryEntityAction } from './EntityActions/RevisionHistoryEntityAction';
import { RunExtractRefreshEntityAction } from './EntityActions/RunExtractRefreshEntityAction';
import { RunFlowEntityAction } from './EntityActions/RunFlowEntityAction';
import { SetCertificationAction } from './EntityActions/SetCertificationAction';
import { SetContactAction } from './EntityActions/SetContactAction';
import { SetDataQualityAction } from './EntityActions/SetDataQualityAction';
import { SetDescriptionAction } from './EntityActions/SetDescriptionAction';
import { SetDisplayTabsAction } from './EntityActions/SetDisplayTabsAction';
import { SetExtractRefreshPriorityEntityAction } from './EntityActions/SetExtractRefreshPriorityEntityAction';
import { SetOwnerAction } from './EntityActions/SetOwnerAction';
import { SetPermissionsAction } from './EntityActions/SetPermissionsAction';
import { SetTagsAction } from './EntityActions/SetTagsAction';
import { ShareContentAction } from './EntityActions/ShareContentAction';
import { UnsubsribeFromSubscriptionsEntityAction } from './EntityActions/UnsubscribeFromSubscriptionsEntityAction';
import { WhoHasSeenThisViewEntityAction } from './EntityActions/WhoHasSeenThisViewEntityAction';

interface IEntityActionListEntry<T extends ExplorableItemType> {
  action: IEntityAction<IExplorableItemTypeInfo<T>, any>;
  supportsMultipleItems: boolean;
  contentTypes: KeySet<T>;
}


/**
 * This function does nothing useful at runtime, but it enforces compile-time self-consistency of the
 * type parameter for each list entry, particularly that the `action` and `contentTypes` are both
 * applicable to the same values of the type parameter T, since we can't achieve that specificity
 * in the array type declaration for ContentActionsSections.
 */
function validEntry<T extends ExplorableItemType>(entry: IEntityActionListEntry<T>): IEntityActionListEntry<T> {
  return entry;
}

/**
 * This is a list of all the entity actions that can appear in actions menus (in explorers or mastheads).
 * The top level arrays denote "sections" separated in the menu by a horizontal divider.
 *
 * When the actions menu is displayed, this list gets filtered to show only the actions that apply to the selected items.
 */
const ContentActionsSections: IEntityActionListEntry<ExplorableItemType>[][] = [
  [
    validEntry({ action: NewWorkbookEntityAction,        supportsMultipleItems: false, contentTypes: makeKeySet('datasource')}),
    validEntry({ action: NewMetricEntityAction,          supportsMultipleItems: false, contentTypes: makeKeySet('datasource')}),
    validEntry({ action: EditFlowEntityAction,           supportsMultipleItems: false, contentTypes: makeKeySet('flow')}),
    validEntry({ action: RunFlowEntityAction,            supportsMultipleItems: true,  contentTypes: makeKeySet('flow')}),
  ],
  [
    validEntry({ action: EditWorkbookAction,             supportsMultipleItems: false, contentTypes: makeKeySet('workbook')}),
    validEntry({ action: EditViewAction,                 supportsMultipleItems: false, contentTypes: makeKeySet('view')}),
  ],
  [
    validEntry({ action: ShareContentAction,             supportsMultipleItems: false, contentTypes: makeKeySet('workbook', 'project', 'view', 'datasource', 'flow')}),
    validEntry({ action: DownloadEntityAction,           supportsMultipleItems: false, contentTypes: makeKeySet('workbook', 'datasource', 'flow')}),
  ],
  [
    validEntry({ action: SetTagsAction,                  supportsMultipleItems: true,  contentTypes: makeKeySet('workbook', 'datasource', 'view', 'flow')}),
    validEntry({ action: RenameEntityAction,             supportsMultipleItems: false, contentTypes: makeKeySet('workbook', 'project', 'flow')}),
    validEntry({ action: MoveAction,                     supportsMultipleItems: true,  contentTypes: makeKeySet('workbook', 'project', 'datasource', 'flow', 'dataRole')}),

    validEntry({ action: SetDescriptionAction,           supportsMultipleItems: false, contentTypes: makeKeySet('database', 'table') }),

    // Set Permissions can be performed on multiple items of the same type, but not multiple items of different types,
    // so we're representing it as separate list entries to facilitate that.
    validEntry({ action: SetPermissionsAction,           supportsMultipleItems: true,  contentTypes: makeKeySet('workbook')}),
    validEntry({ action: SetPermissionsAction,           supportsMultipleItems: true,  contentTypes: makeKeySet('project')}),
    validEntry({ action: SetPermissionsAction,           supportsMultipleItems: true,  contentTypes: makeKeySet('datasource')}),
    validEntry({ action: SetPermissionsAction,           supportsMultipleItems: true,  contentTypes: makeKeySet('view')}),
    validEntry({ action: SetPermissionsAction,           supportsMultipleItems: true,  contentTypes: makeKeySet('flow')}),
    validEntry({ action: SetPermissionsAction,           supportsMultipleItems: true,  contentTypes: makeKeySet('dataRole')}),
    validEntry({ action: SetPermissionsAction,           supportsMultipleItems: true,  contentTypes: makeKeySet('table')}),
    validEntry({ action: SetPermissionsAction,           supportsMultipleItems: true,  contentTypes: makeKeySet('database')}),

    validEntry({ action: WhoHasSeenThisViewEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('view')}),

    validEntry({ action: SetOwnerAction,                 supportsMultipleItems: true,  contentTypes: makeKeySet('workbook', 'project', 'datasource', 'flow', 'dataRole')}),
    validEntry({ action: SetContactAction,             supportsMultipleItems: false, contentTypes: makeKeySet('database', 'table') }),
  ],
  [
    validEntry({ action: SetCertificationAction,         supportsMultipleItems: false, contentTypes: makeKeySet('database', 'table') }),
    validEntry({ action: SetDataQualityAction,           supportsMultipleItems: false, contentTypes: makeKeySet('datasource', 'flow', 'database', 'table') }),
  ],
  [
    validEntry({ action: RemoveExternalContentAction,    supportsMultipleItems: false, contentTypes: makeKeySet('database', 'table') }),
  ],
  [
    validEntry({ action: EditDataConnectionsAction,      supportsMultipleItems: true,  contentTypes: makeKeySet('datasource', 'dataconnection')}),
    validEntry({ action: SetDisplayTabsAction,           supportsMultipleItems: true,  contentTypes: makeKeySet('workbook')}),
    validEntry({ action: CreateExtractsAction,           supportsMultipleItems: true,  contentTypes: makeKeySet('workbook', 'datasource')}),
    validEntry({ action: RefreshExtractsAction,          supportsMultipleItems: true,  contentTypes: makeKeySet('workbook', 'datasource')}),
    validEntry({ action: EncryptAction,                  supportsMultipleItems: true,  contentTypes: makeKeySet('workbook', 'datasource', 'flow')}),
    validEntry({ action: DecryptAction,                  supportsMultipleItems: true,  contentTypes: makeKeySet('workbook', 'datasource', 'flow')}),
    validEntry({ action: ChangeExtractDsToLiveAction,    supportsMultipleItems: true,  contentTypes: makeKeySet('datasource')}),
  ],
  [
    validEntry({ action: RevisionHistoryEntityAction,    supportsMultipleItems: false, contentTypes: makeKeySet('workbook', 'datasource', 'flow')}),
    validEntry({ action: DeleteAction,                   supportsMultipleItems: true,  contentTypes: makeKeySet('workbook', 'project', 'datasource', 'view', 'flow', 'dataRole')}),
  ],
  [
    validEntry({ action: ResumeSubscriptionEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('subscription')}),
    validEntry({ action: ChangeSubscriptionScheduleEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('subscription')}),
    validEntry({ action: ChangeSubscriptionSubjectEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('subscription')}),
    validEntry({ action: ChangeSubscriptionDataConditionEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('subscription')}),
    validEntry({ action: ChangeSubscriptionsFormatEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('subscription')}),
    validEntry({ action: UnsubsribeFromSubscriptionsEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('subscription')}),
  ],
  [
    validEntry({ action: RunExtractRefreshEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('extractTask') }),
    validEntry({ action: ChangeExtractRefreshScheduleEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('extractTask') }),
    validEntry({ action: SetExtractRefreshPriorityEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('extractTask') }),
    validEntry({ action: DeleteExtractRefreshEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('extractTask') }),
  ],
  [
    validEntry({ action: DeleteRemoteExtractRefreshEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('remoteRefreshSchedule') }),
    validEntry({ action: ChangeRemoteExtractRefreshEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('remoteRefreshSchedule') }),
    validEntry({ action: ChangeRemoteExtractRefreshExpectedDurationEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('remoteRefreshSchedule') }),
  ],
  [
    validEntry({ action: CancelBackgroundJobAction,     supportsMultipleItems: false, contentTypes: makeKeySet('backgroundJob') }),
  ]
];


export function getActionSectionsForItems(...items: IExplorableItemTypeInfo[]): IEntityAction<IExplorableItemTypeInfo, any>[][] {
  const itemTypes = makeKeySet(...items.map(item => item.contentType));

  const entityActionSections = ContentActionsSections.map(section => {
    return section.filter(info => isSubsetOf(itemTypes, info.contentTypes))
                  .filter(info => info.supportsMultipleItems || items.length === 1)
                  .map(info => info.action);
  });

  return entityActionSections.filter(section => !_(section).isEmpty());
}
